import { useTranslation } from 'react-i18next';
import { css, keyframes } from 'styled-components';
import styled from 'styled-components';

import CloseIconSvg from '../../../assets/icons/icon-close.svg?react';
import { zIndexMap } from '../../../shared/config';
import { useSheetPresence } from '../../../shared/hooks/use-sheet-presence';
import { BaseButton, nonSelectableSurfaceStyles } from '../../../shared/ui';
import { AvatarEditorFlow } from '../../avatar-editor/ui';

type MyPageAvatarEditorOverlayProps = {
  avatarUrl?: string | null;
  isOpen: boolean;
  onClose: () => void;
};

const CLOSE_ANIMATION_MS = 180;

const overlayEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(100%);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const overlayExit = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }

  to {
    opacity: 0;
    transform: translateY(100%);
  }
`;

const Overlay = styled.div.attrs({
  'data-ui': 'my-page-avatar-editor-overlay',
})<{ $isOpen: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.surface.base};
  z-index: ${zIndexMap.myPageProfile.avatarEditorOverlay};
  ${nonSelectableSurfaceStyles};
  ${({ $isOpen }) =>
    $isOpen
      ? css`
          animation: ${overlayEnter} 300ms ease-out both;
        `
      : css`
          animation: ${overlayExit} ${CLOSE_ANIMATION_MS}ms ease-in both;
        `};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const TopBar = styled.div.attrs({
  'data-ui': 'my-page-avatar-editor-top-bar',
})`
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  padding-top: ${({ theme }) =>
    `max(${theme.scale.px(12)}, env(safe-area-inset-top))`};
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(18)}, env(safe-area-inset-right))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(18)}, env(safe-area-inset-left))`};
`;

const CloseButton = styled(BaseButton).attrs({
  type: 'button',
})`
  width: ${({ theme }) => theme.scale.px(32)};
  height: ${({ theme }) => theme.scale.px(32)};
  display: grid;
  place-items: center;
  border-radius: 50%;
`;

const CloseIcon = styled(CloseIconSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: ${({ theme }) => theme.scale.px(18)};
  height: ${({ theme }) => theme.scale.px(18)};
  color: ${({ theme }) => theme.colors.text.primary};

  & path {
    stroke: currentColor;
  }
`;

const Content = styled.div.attrs({
  'data-ui': 'my-page-avatar-editor-content',
})`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export function MyPageAvatarEditorOverlay({
  avatarUrl = null,
  isOpen,
  onClose,
}: MyPageAvatarEditorOverlayProps) {
  const { t } = useTranslation();
  const { handleCloseAnimationEnd, isMounted } = useSheetPresence(isOpen);

  if (!isMounted) {
    return null;
  }

  return (
    <Overlay $isOpen={isOpen} onAnimationEnd={handleCloseAnimationEnd}>
      <TopBar>
        <CloseButton
          aria-label={t('myPage.avatarEditor.closeAria')}
          onClick={onClose}
        >
          <CloseIcon />
        </CloseButton>
      </TopBar>
      <Content>
        <AvatarEditorFlow
          initialAvatarUrl={avatarUrl}
          onComplete={onClose}
          variant="profile"
        />
      </Content>
    </Overlay>
  );
}
